import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { setLanguage, LANGUAGES } from '../utils/localization/localization';
import { logger } from '../utils/logger';
import LocalStorageService from '../utils/localStorageService';

// Simple language localStorage key
const LANGUAGE_STORAGE_KEY = 'autobot_language';

// Create the context
const LanguageContext = createContext();

// Custom hook to use the language context
export const useLanguage = () => {
  return useContext(LanguageContext);
};

// Provider component
export const LanguageProvider = ({ children }) => {
  // Add renderTrigger state to force re-renders when language changes
  const [renderTrigger, setRenderTrigger] = useState(Date.now());
  const [loading, setLoading] = useState(true);
  const [initializationError, setInitializationError] = useState(null);

  const [currentLanguage, setCurrentLanguage] = useState(LANGUAGES.FR); // Default to French initially

  // Save language to localStorage - memoized
  const saveLanguageToStorage = useCallback((language) => {
    try {
      LocalStorageService.setItem(LANGUAGE_STORAGE_KEY, language);
      logger.info('[LANGUAGE]', `Saved language ${language} to localStorage`);
      return true;
    } catch (error) {
      logger.error('[LANGUAGE]', 'Error saving language to localStorage:', error);
      return false;
    }
  }, []);

  // Get language from localStorage - memoized
  const getLanguageFromStorage = useCallback(() => {
    try {
      const language = LocalStorageService.getItem(LANGUAGE_STORAGE_KEY);
      logger.info('[LANGUAGE]', `Retrieved language ${language} from localStorage`);
      return language;
    } catch (error) {
      logger.error('[LANGUAGE]', 'Error getting language from localStorage:', error);
      return null;
    }
  }, []);

  // Force update function to trigger re-renders - memoized without currentLanguage dependency
  const forceUpdate = useCallback(() => {
    // Trigger state update to force React components to re-render
    setRenderTrigger(Date.now());
  }, []);

  // Initialize language on mount
  useEffect(() => {
    const initializeLanguage = () => {
      try {
        // Try to get language from localStorage
        const storedLanguage = getLanguageFromStorage();
        logger.info('[LANGUAGE_CONTEXT]', 'Loading language from storage:', storedLanguage);

        let selectedLanguage;
        if (storedLanguage && Object.values(LANGUAGES).includes(storedLanguage)) {
          logger.info('[LANGUAGE_CONTEXT]', 'Successfully loaded language:', storedLanguage);
          selectedLanguage = storedLanguage;
        } else {
          // Default to French as per requirements
          const _browserLanguage = navigator.language.substring(0, 2).toLowerCase();
          selectedLanguage = LANGUAGES.FR; // Always default to French

          // Note: Uncomment the line below if you want to respect browser language preference
          // if (browserLanguage === 'en') selectedLanguage = LANGUAGES.EN;

          logger.info('[LANGUAGE_CONTEXT]', 'Using default language (French):', selectedLanguage);
          // Save the default language to localStorage
          saveLanguageToStorage(selectedLanguage);
        }

        // Batch state updates
        setCurrentLanguage(selectedLanguage);
        // Make sure the localization utility is in sync
        setLanguage(selectedLanguage);
        setLoading(false);
      } catch (error) {
        logger.error('[LANGUAGE_CONTEXT]', 'Error loading language:', error);
        setInitializationError(error);
        setLoading(false);
      }
    };

    initializeLanguage();
  }, [getLanguageFromStorage, saveLanguageToStorage]); // Add required dependencies

  // Toggle language between English and French - memoized
  const toggleLanguage = useCallback(async () => {
    const newLanguage = currentLanguage === LANGUAGES.EN ? LANGUAGES.FR : LANGUAGES.EN;
    const success = await setLanguage(newLanguage);
    if (success) {
      // Set flag to indicate language change is in progress
      window.languageChangeInProgress = true;

      setCurrentLanguage(newLanguage);
      // Save to localStorage
      saveLanguageToStorage(newLanguage);
      const event = new CustomEvent('languageChanged',{
        detail: { language: newLanguage, timestamp: Date.now() }
      });
      window.dispatchEvent(event);
      logger.info('[LANGUAGE_CONTEXT]', 'Language changed to:', newLanguage);
      forceUpdate();

      // Clear the flag after a short delay to allow all effects to complete
      setTimeout(() => {
        window.languageChangeInProgress = false;
      }, 1000);
    }
  }, [currentLanguage, saveLanguageToStorage, forceUpdate]);

  // Set a specific language - memoized
  const changeLanguage = useCallback(async (language) => {
    const success = await setLanguage(language);
    if (success) {
      // Set flag to indicate language change is in progress
      window.languageChangeInProgress = true;

      setCurrentLanguage(language);
      // Save to localStorage
      saveLanguageToStorage(language);
      const event = new CustomEvent('languageChanged',{
        detail: { language, timestamp: Date.now() }
      });
      window.dispatchEvent(event);
      logger.info('[LANGUAGE_CONTEXT]', 'Language changed to:', language);
      forceUpdate();

      // Clear the flag after a short delay to allow all effects to complete
      setTimeout(() => {
        window.languageChangeInProgress = false;
      }, 1000);

      return true;
    }
    return false;
  }, [saveLanguageToStorage, forceUpdate]);

  // Listen for language changes from other parts of the app
  useEffect(() => {
    const handleLanguageChange = (event) => {
      logger.info('[LANGUAGE_CONTEXT]', 'Language change event received', event.detail);
      // Update current language if it's different
      if (event.detail.language && event.detail.language !== currentLanguage) {
        setCurrentLanguage(event.detail.language);
        setLanguage(event.detail.language);
        // Force re-render when language changes
        forceUpdate();
      }
    };

    window.addEventListener('languageChanged', handleLanguageChange);

    return () => {
      window.removeEventListener('languageChanged', handleLanguageChange);
    };
  }, [forceUpdate, currentLanguage]);

  // Context value
  const value = {
    currentLanguage,
    isEnglish: currentLanguage === LANGUAGES.EN,
    isFrench: currentLanguage === LANGUAGES.FR,
    toggleLanguage,
    changeLanguage,
    renderTrigger // Add renderTrigger to context value so components can depend on it
  };

  if (initializationError) {
    throw initializationError;
  }

  // Wait until language is initialized
  if (loading) {
    return null;
  }

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export default LanguageContext;
